// src/components/student/SaveButton.jsx
// Star toggle to save a test or paper to the student's Library.
// Students-only (hidden for guests). Optimistic UI with rollback on failure.
import React, { useEffect, useState } from "react";
import { HiBookmark, HiOutlineBookmark } from "react-icons/hi";
import { useAuth } from "../../context/AuthContext";
import {
  isTestSaved, toggleSavedTest, isPaperSaved, toggleSavedPaper,
} from "../../lib/bookmarks";

// kind: "test" | "paper"; item: the mcq or resource object (must have .id)
export default function SaveButton({ kind, item, variant = "icon", className = "" }) {
  const { user, role } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user || role !== "student" || !item?.id) { setReady(false); return undefined; }
    (async () => {
      try {
        const s = kind === "paper"
          ? await isPaperSaved(user.uid, item.id)
          : await isTestSaved(user.uid, item.id);
        if (active) { setSaved(s); setReady(true); }
      } catch { if (active) setReady(true); }
    })();
    return () => { active = false; };
  }, [user, role, kind, item?.id]);

  // Only students see the save control.
  if (role !== "student" || !user || !item?.id) return null;

  const onToggle = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next); // optimistic
    try {
      const result = kind === "paper"
        ? await toggleSavedPaper(user.uid, item)
        : await toggleSavedTest(user.uid, item);
      setSaved(result);
    } catch {
      setSaved(!next); // rollback
    } finally {
      setBusy(false);
    }
  };

  const Icon = saved ? HiBookmark : HiOutlineBookmark;

  if (variant === "button") {
    return (
      <button
        onClick={onToggle}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-xl border transition-colors ${
          saved
            ? "bg-amber-50 border-amber-200 text-amber-700"
            : "bg-white border-gray-200 text-gray-500 hover:border-amber-200 hover:text-amber-600"
        } ${className}`}
        title={saved ? "Remove from Library" : "Save to Library"}
      >
        <Icon className="text-base" /> {saved ? "Saved" : "Save"}
      </button>
    );
  }

  return (
    <button
      onClick={onToggle}
      disabled={busy}
      aria-label={saved ? "Remove from Library" : "Save to Library"}
      title={saved ? "Remove from Library" : "Save to Library"}
      className={`flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
        saved ? "text-amber-500 hover:bg-amber-50" : "text-gray-400 hover:bg-gray-100 hover:text-amber-500"
      } ${!ready ? "opacity-60" : ""} ${className}`}
    >
      <Icon className="text-xl" />
    </button>
  );
}
